import {
  requestViaBackendProxy,
  extractHaitangwMedia,
  buildMediaSourceResult
} from './musicPluginProxy.js';
import { enrichWySearchCovers, resolveWyCoverFromItem } from './plazaCoverUtils.js';

const PAGE_SIZE = 20;
const PLATFORM = 'wy';
const AUDIO_PATH = 'wy_song_kw.php';

const QUALITY_LEVELS = {
  low: '128',
  standard: '128',
  high: '320',
  super: '320'
};

const SEARCH_HEADERS = {
  Referer: 'https://music.163.com/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

const REQUEST_HEADERS = {
  Referer: 'https://music.163.com/',
  'User-Agent':
    'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'
};

function normalizeSong(item) {
  if (!item) return null;

  const artists = Array.isArray(item.artists)
    ? item.artists.map((artist) => artist.name).filter(Boolean)
    : [];

  const cover = resolveWyCoverFromItem(item);

  return {
    id: item.id,
    title: item.name || item.title || '未知歌曲',
    artist: artists.join(', ') || item.artist || '未知艺术家',
    album: item.al?.name || item.album?.name || '',
    duration: Math.round((item.duration || 0) / 1000),
    coverUrl: cover,
    artwork: cover,
    source: 'wy-plugin',
    raw: item
  };
}

async function search(query, page = 1) {
  if (!query?.trim()) {
    throw new Error('搜索关键词不能为空');
  }

  const offset = (page - 1) * PAGE_SIZE;
  const url = `https://music.163.com/api/search/get/web?s=${encodeURIComponent(query.trim())}&type=1&limit=${PAGE_SIZE}&offset=${offset}`;
  const body = await requestViaBackendProxy(url, SEARCH_HEADERS);
  const songs = body?.result?.songs || [];
  const total = Number(body?.result?.songCount ?? songs.length);

  const data = songs.map(normalizeSong).filter(Boolean);
  await enrichWySearchCovers(data);

  return {
    isEnd: offset + data.length >= total || data.length < PAGE_SIZE,
    total,
    data
  };
}

async function getMediaSourceFromHaitangw(songId, qualityLevel, musicItem, quality) {
  const audioUrl = `https://music.haitangw.cc/music/${AUDIO_PATH}?id=${songId}&quality=${qualityLevel}`;
  const responseData = await requestViaBackendProxy(audioUrl, REQUEST_HEADERS);
  const audioData = extractHaitangwMedia(responseData);
  return buildMediaSourceResult(audioData, musicItem, quality);
}

async function getLyricFromNetease(songId) {
  const lyricUrl = `https://music.163.com/api/song/lyric?id=${encodeURIComponent(songId)}&lv=1&kv=-1&tv=-1`;
  const body = await requestViaBackendProxy(lyricUrl, SEARCH_HEADERS);
  const lrcText = body?.lrc?.lyric || body?.lyric || '';

  if (!lrcText || !String(lrcText).trim()) {
    throw new Error('网易官方歌词为空');
  }

  return {
    lrc: lrcText,
    tlyric: body?.tlyric?.lyric || '',
    rlyric: body?.klyric?.lyric || ''
  };
}

async function getMediaSourceFromNetease(songId, musicItem, quality) {
  const levelMap = {
    low: 'standard',
    standard: 'standard',
    high: 'exhigh',
    super: 'lossless'
  };
  const level = levelMap[quality] || 'exhigh';
  const ids = encodeURIComponent(JSON.stringify([Number(songId) || songId]));
  const playUrl = `https://music.163.com/api/song/enhance/player/url/v1?ids=${ids}&level=${level}&encodeType=mp3`;
  const body = await requestViaBackendProxy(playUrl, SEARCH_HEADERS);
  const item = body?.data?.[0];
  const url = item?.url || item?.freeTrialInfo?.url;

  if (!url || String(url).includes('404')) {
    throw new Error('网易官方播放接口未返回有效URL');
  }

  const result = {
    url,
    quality,
    duration: musicItem?.duration || 0,
    headers: {}
  };

  try {
    result.lyric = await getLyricFromNetease(songId);
  } catch (lyricError) {
    console.warn('[wyPluginClient] 网易官方歌词获取失败:', lyricError.message);
  }

  return result;
}

async function getMediaSource(musicItem, quality = 'high') {
  const songId = musicItem?.id || musicItem?.raw?.id;
  if (!songId) {
    throw new Error('网易歌曲ID不能为空');
  }

  const qualityCandidates = [
    QUALITY_LEVELS[quality] || QUALITY_LEVELS.high,
    QUALITY_LEVELS.standard,
    QUALITY_LEVELS.low
  ].filter((value, index, array) => array.indexOf(value) === index);

  let lastError = null;
  for (const qualityLevel of qualityCandidates) {
    try {
      const result = await getMediaSourceFromHaitangw(songId, qualityLevel, musicItem, quality);
      if (!result.lyric?.lrc) {
        try {
          result.lyric = await getLyricFromNetease(songId);
        } catch (lyricError) {
          console.warn('[wyPluginClient] 网易官方歌词获取失败:', lyricError.message);
        }
      }
      return result;
    } catch (error) {
      lastError = error;
      console.warn(`[wyPluginClient] 海棠网 quality=${qualityLevel} 获取失败:`, error.message);
    }
  }

  try {
    console.warn('[wyPluginClient] 海棠网全部失败，回退网易官方播放接口');
    return await getMediaSourceFromNetease(songId, musicItem, quality);
  } catch (neteaseError) {
    console.warn('[wyPluginClient] 网易官方播放接口失败:', neteaseError.message);
    throw lastError || neteaseError;
  }
}

async function getLyric(musicItem, options = {}) {
  if (options.cachedLyric?.lrc) {
    return options.cachedLyric;
  }

  const songId = musicItem?.id || musicItem?.raw?.id;
  if (!songId) {
    throw new Error('网易歌曲ID不能为空');
  }

  try {
    const result = await getMediaSource(musicItem, 'high');
    if (result.lyric?.lrc) {
      return result.lyric;
    }
  } catch (error) {
    console.warn('[wyPluginClient] 通过播放接口获取歌词失败:', error.message);
  }

  return getLyricFromNetease(songId);
}

const wyPluginClient = {
  platform: PLATFORM,
  version: '1.0.0',
  search,
  getMediaSource,
  getLyric
};

export default wyPluginClient;
